import React, { useEffect, useState, useRef } from 'react';
import { Form, Col } from 'reactstrap';
import './NewTravelRequest.scss';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiChevronLeft } from '@mdi/js';
import {
  initialValues,
  renderStatusColors,
  validationSchema,
  FIELD_ORDER,
  FLIGHT_ITEM_FIELDS,
  HOTEL_ITEM_FIELDS,
  CLIENT_ITEM_FIELDS,
} from './constant';
import {
  ClientInformation,
  FlightInformation,
  HotelInformation,
  NewFamilyDetailsModal,
  UploadAttachments,
  ViewApproversModal,
  ViewPOModal,
  ViewRevisionsModal,
  CancelQuoteRequest,
  CancelTravelRequest,
  ModifyBooking,
  QuotePendingModal,
  CloneRequestModal,
  RiskCountryModal,
  RiskCountryDetails,
  CancelBooking,
} from '../../../components/TravelRequest';
import {
  fetchDefault,
  fetchTravelRequestDetails,
  fetchTravelRequestOptions,
  saveTravelRequestDetails,
} from '../../../store/apis/travelRequestService';
import AdditionalInformation from '../../../components/TravelRequest/AdditionalInformation/AdditionalInformation';
import EmergencyContact from '../../../components/TravelRequest/EmergencyContact/EmergencyContact';
import ShowCost from '../../../components/TravelRequest/ShowCost/ShowCost';
import GeneralInformation from '../../../components/TravelRequest/GeneralInformation/GeneralInformation';
import {
  fetchCommonOptions,
  selectOption,
} from '../../../store/apis/travelService';
import Requirements from '../../../components/TravelRequest/Requirements/Requirements';
import { renderButton } from './NewTravelRequestUtil';
import { Badge } from '../../../components/Common';

function NewTravelRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const [searchParams] = useSearchParams();
  const requestMode = searchParams.get('m') || null;
  const formRef = useRef(null);

  const isClone = requestMode === 'Clone';
  const isView = requestMode === 'View';
  const isEdit = location?.pathname?.includes('/edit');
  const isHr = location?.pathname?.includes('/hr');
  const isFromEnquiry = state?.from === 'TravelEnquiry';

  document.title = `${isEdit ? 'Edit' : 'New'} Travel Request | Envision Daikin`;

  const { requestNo, revisionNo } = useParams();
  const [submitOption, setSubmitOption] = useState('S');

  const isParamsReady = Boolean(requestNo && revisionNo && isEdit);

  // Fetch common options when travelType changes
  const { data: commonOptions = [], isLoading: isCommonOptionsLoading } =
    useQuery({
      queryKey: ['commonOptions'],
      queryFn: fetchCommonOptions,
      staleTime: 5 * 60 * 1000, // optional: cache for 5 minutes
    });

  const { data: requestOptions } = useQuery({
    queryKey: ['requestOptions', requestNo, revisionNo],
    queryFn: () => fetchTravelRequestOptions({ requestNo, revisionNo }),
    enabled: isParamsReady,
    // ensure a refetch when component mounts / route is revisited
    refetchOnMount: 'always', // Refetch when component mounts
    refetchOnWindowFocus: false, // Prevent unwanted refetches
    onError: () => {
      toast.error('Failed to fetch travel request options', {
        className: 'toast-error',
      });
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues,
    setFocus,
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  // Watch the travelType field
  const travelType = watch('travelType');
  const travellerType = watch('travellerType');
  const requirements = watch('requirements');

  const { data: defaultData } = useQuery({
    queryKey: ['fetchDefault'],
    queryFn: () => fetchDefault(),
    enabled: !isEdit,
  });

  const {
    isLoading: detailsLoading,
    data: detailsData,
    refetch: refetchTravelRequestDetails,
  } = useQuery({
    queryKey: ['fetchTravelRequestDetails', requestNo, revisionNo],
    queryFn: () => fetchTravelRequestDetails({ requestNo, revisionNo }),
    enabled: isParamsReady, // only fetch when modal is open
    onError: () => {
      toast.error('Failed to fetch travel request details', {
        className: 'toast-error',
      });
    },
  });

  const { mutate: submitRequest, isPending: isSaveSubmitting } = useMutation({
    mutationFn: saveTravelRequestDetails,
    onSuccess: response => {
      if (response?.success) {
        if (submitOption === 'Q') {
          toast.success('Request for quote submitted successfully', {
            className: 'toast-success',
          });
        } else {
          toast.success(
            `Travel Request No #${response?.requestNo} saved successfully!`,
            { className: 'toast-success' },
          );
        }
        navigate(
          `/travel-request/edit/${response?.requestNo}/${response?.revisionNo}`,
        );
      } else if (response?.errorData && response?.errorData.length > 0) {
        response.errorData.forEach(error => {
          toast.error(error[0]['Error Message'], {
            className: 'toast-error',
          });
        });
      } else {
        toast.error(response.message || 'Submission failed', {
          className: 'toast-error',
        });
      }
    },
    onError: error => {
      toast.error(error.message || 'Submission failed', {
        className: 'toast-error',
      });
    },
  });

  useEffect(() => {
    if (isParamsReady) {
      refetchTravelRequestDetails();
    }
  }, [
    requestNo,
    revisionNo,
    isEdit,
    refetchTravelRequestDetails,
    isParamsReady,
  ]);

  useEffect(() => {
    if (defaultData?.[0]) {
      const data = defaultData?.[0];
      setValue('travellerFirstName', data?.Fname ?? '');
      setValue('travellerMiddleName', data?.Mname ?? '');
      setValue('travellerLastName', data?.Lname ?? '');
      setValue('contactName', data?.EmergencyConPerson ?? '');
      setValue('contactNumber', data?.EmergencyConNo ?? '');
    }
  }, [defaultData, setValue]);

  useEffect(() => {
    if (detailsData?.headerDetails) {
      const headerDetails = detailsData?.headerDetails;
      setValue('travellerFirstName', headerDetails?.Traveler_FName ?? '');
      setValue('travellerMiddleName', headerDetails?.Traveler_MName ?? '');
      setValue('travellerLastName', headerDetails?.Traveler_LName ?? '');
      setValue('contactName', headerDetails?.Emergency_Name ?? '');
      setValue('contactNumber', headerDetails?.Emergency_ContactNo ?? '');
    }
  }, [detailsData, setValue]);

  useEffect(() => {
    const subscription = watch((_value, { name }) => {
      if (name === 'travelType') {
        // Reset travellerType when travelType changes
        setValue('travellerType', null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const [familyDetailsModal, setFamilyDetailsModal] = useState(false);
  const [viewPOModal, setViewPOModal] = useState(false);
  const [viewRevisionsModal, setViewRevisionsModal] = useState(false);
  const [viewApproversModal, setViewApproversModal] = useState(false);
  const [cancelQuoteModal, setCancelQuoteModal] = useState(false);
  const [cancelTravelRequestModal, setCancelTravelRequestModal] =
    useState(false);
  const [modifyBookingModal, setModifyBookingModal] = useState(false);
  const [quotePendingModal, setQuotePendingModal] = useState(false);
  const [cloneModal, setCloneModal] = useState(false);
  const [riskCountryModal, setRiskCountryModal] = useState(false);
  const [riskCountryDetailsModal, setRiskCountryDetailsModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [riskCountryName, setRiskCountryName] = useState('');
  const [cancelBookingModal, setCancelBookingModal] = useState(false);

  const travelStatus =
    detailsData?.headerDetails?.TravelStatus?.toString()?.trim() ?? null;
  const showSave =
    travelStatus === null ||
    ['01', '10', '12']?.includes(travelStatus) ||
    isClone;
  const showRequestForQuote =
    ['01', '10', '12']?.includes(travelStatus) ?? false;
  const showCancelQuoteRequest = ['05']?.includes(travelStatus) ?? false;
  const showCancelTravelRequest =
    ['05', '10', '12']?.includes(travelStatus) ?? false;
  const showSubmitForApproval = ['12']?.includes(travelStatus) ?? false;
  const showViewQuoteOption =
    ['10', '12', '30', '15', '20', '90', '16']?.includes(travelStatus) ?? false;
  const showModifyBooking = ['30']?.includes(travelStatus) ?? false;
  const showCancelBooking = ['30']?.includes(travelStatus) ?? false;
  const showViewApprovers =
    ['30', '15', '20', '90', '16', '90']?.includes(travelStatus) ?? false;
  const showViewRiskCountryInfo = detailsData?.riskDetails?.length > 0;
  const showViewPODetails =
    ['30', '15', '20', '90', '16', '90']?.includes(travelStatus) ?? false;
  const showCost = (isEdit && !['01', '05']?.includes(travelStatus)) ?? false;

  const toggleFamilyDetailsModal = () => {
    setFamilyDetailsModal(!familyDetailsModal);
  };

  const togglePOModal = () => {
    setViewPOModal(!viewPOModal);
  };

  const toggleRevisionsModal = () => {
    setViewRevisionsModal(!viewRevisionsModal);
  };

  const toggleApproversModal = () => {
    setViewApproversModal(!viewApproversModal);
  };

  const toggleCancelQuoteModal = () => {
    setCancelQuoteModal(!cancelQuoteModal);
  };

  const toggleCancelTravelRequestModal = () => {
    setCancelTravelRequestModal(!cancelTravelRequestModal);
  };

  const toggleModifyBookingModal = () => {
    setModifyBookingModal(!modifyBookingModal);
  };

  const toggleQuotePendingModal = React.useCallback(() => {
    setQuotePendingModal(prev => !prev);
  }, []);

  const toggleCloneModal = () => {
    setCloneModal(!cloneModal);
  };

  const toggleRiskCountryModal = () => {
    setRiskCountryModal(!riskCountryModal);
  };

  const toggleRiskCountryDetailsModal = () => {
    setRiskCountryDetailsModal(!riskCountryDetailsModal);
  };

  const toggleCancelBookingModal = () => {
    setCancelBookingModal(!cancelBookingModal);
  };

  useEffect(() => {
    if (travelStatus === '10' && !isView) {
      setQuotePendingModal(true);
    } else {
      setQuotePendingModal(false);
    }
  }, [travelStatus, toggleQuotePendingModal]);

  const checkForRiskCountry = payload => {
    const getCountryName = (item, key) =>
      item?.[key]?.label || item?.[key]?.value || null;

    const riskFlight = payload.flightInformation?.find(
      f => f.isRiskCountry === 'Y',
    );

    if (riskFlight) {
      return getCountryName(riskFlight, 'toCountry');
    }

    const riskHotel = payload.hotelInformation?.find(
      h => h.isRiskCountry === 'Y',
    );
    if (riskHotel) {
      return getCountryName(riskHotel, 'country');
    }

    return null;
  };

  const onSubmit = data => {
    const payload = {
      ...data,
      sorNo: data.sorNo?.value ?? '',
      serviceOrder: data.serviceOrder?.value ?? '',
    };
    if (isClone) {
      payload.requestNo = '';
      payload.revisionNo = 0;
    } else if (isEdit) {
      payload.requestNo = requestNo;
      payload.revisionNo = revisionNo;
    }
    payload.updateOption = submitOption;
    // Check for any risk countries
    const riskCountry = checkForRiskCountry(payload);
    if (riskCountry) {
      if (submitOption === 'S') {
        setPendingPayload(payload);
        setRiskCountryName(riskCountry);
        setRiskCountryModal(true);
        return;
      }
      if (submitOption === 'Q') {
        toggleRiskCountryDetailsModal();
        setPendingPayload(payload);
        return;
      }
    }

    submitRequest(payload);
  };

  const { mutate: selectTravelOption, isPending: selectTravelOptionPending } =
    useMutation({
      mutationFn: async payload => selectOption(payload),
      onSuccess: response => {
        if (response?.flag === 'True') {
          toast.success('Option selected successfully!', {
            className: 'toast-success',
          });
          navigate('/travel-request');
        } else if (response?.flag === 'False') {
          toast.error(response?.message || 'Failed to select option!', {
            className: 'toast-error',
          });
        }
      },
    });

  const handleSubmitApproval = () => {
    const payload = {
      reqNo: requestNo,
      revisionNo: parseInt(revisionNo, 10),
      userAction: 'A',
      quoteSelectReason: requestOptions?.headerDetails?.QuoteSelectReason || '',
      hotelOptionNo: requestOptions?.headerDetails?.Selected_Option_Hotel || 0,
      flightOptionNo:
        requestOptions?.headerDetails?.Selected_Option_Flight || 0,
    };
    selectTravelOption(payload);
  };

  function getIn(obj, path) {
    return path
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .reduce((o, key) => (o == null ? undefined : o[key]), obj);
  }

  // 3️⃣ The ordered‐errors function
  // 3️⃣ this is the only function you need to drop in
  function getOrderedErrors(formErrors) {
    const out = [];

    FIELD_ORDER.forEach(field => {
      const node = getIn(formErrors, field);
      if (Array.isArray(node)) {
        let subFields = [];
        if (field === 'flightInformation') {
          subFields = FLIGHT_ITEM_FIELDS;
        } else if (field === 'hotelInformation') {
          subFields = HOTEL_ITEM_FIELDS;
        } else if (field === 'clientInformation') {
          subFields = CLIENT_ITEM_FIELDS;
        }

        node.forEach((_, idx) => {
          subFields.forEach(sub => {
            const fullPath = `${field}[${idx}].${sub}`;
            const cfg = getIn(formErrors, fullPath);
            if (cfg?.message) {
              out.push([fullPath, cfg.message]);
            } else if (
              (field === 'flightInformation' ||
                field === 'hotelInformation' ||
                field === 'clientInformation') &&
              cfg &&
              typeof cfg === 'object'
            ) {
              const msg = cfg.value?.message || cfg.label?.message;
              if (msg) out.push([fullPath, msg]);
            }
          });
        });
      } else if (node?.message) {
        out.push([field, node.message]);
      }
    });

    return out;
  }

  // The onError function
  // Check and scroll to the first error
  /* eslint-disable */
  const onError = errs => {
    // 1️⃣ flatten errs in entry order; for arrays use Object.keys() for nested order
    const errorEntries = Object.entries(errs);
    if (errorEntries.length === 0) return;
    const flat = getOrderedErrors(errs);
    if (!flat.length) return;

    // 2️⃣ pick the first one
    let [firstKey, firstMsg] = flat[0];
    if (!firstKey) return;
    setFocus(firstKey);

    // 3️⃣ scroll/focus logic with robust fallbacks
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const root = formRef.current;
        if (!root) {
          return;
        }

        let target = null;
        const isArray = firstKey.includes('[');
        const message = flat[0][1]?.message || '';

        // Check if this is one of the specific table-based array fields
        const isTableArrayField =
          isArray &&
          (firstKey.startsWith('flightInformation[') ||
            firstKey.startsWith('clientInformation[') ||
            firstKey.startsWith('hotelInformation['));

        // If it's a table-based array field, look for TD first
        if (isTableArrayField) {
          const arrayMatch = firstKey.match(/^(\w+)\[(\d+)\](?:\.(.+))?$/);
          if (arrayMatch) {
            const [, arrName, idx, fieldName] = arrayMatch;
            // Look for table row containing this array index
            const tableRows = root.querySelectorAll('tr');
            for (const row of tableRows) {
              const rowInputs = row.querySelectorAll('input, select, textarea');
              const hasMatchingIndex = Array.from(rowInputs).some(input => {
                const match =
                  input.name &&
                  (input.name.includes(`${arrName}[${idx}]`) ||
                    input.name.includes(`${arrName}.${idx}`));
                return match;
              });

              if (hasMatchingIndex) {
                // Try to find specific field cell if fieldName exists
                if (fieldName) {
                  const cellInputs = row.querySelectorAll(
                    'td input, td select, td textarea',
                  );
                  for (const input of cellInputs) {
                    if (
                      input.name &&
                      (input.name.includes(`.${fieldName}`) ||
                        input.name.includes(`[${fieldName}]`))
                    ) {
                      const inputTd = input.closest('td');
                      target = inputTd;
                      break;
                    }
                  }
                }

                // If we still don't have target, check React-Select components in this row
                if (!target && fieldName) {
                  const reactSelects = row.querySelectorAll(
                    '[class*="react-select__control"]',
                  );
                  reactSelects.forEach((select, selectIndex) => {
                    const selectTd = select.closest('td');
                    const tdIndex = Array.from(row.children).indexOf(selectTd);

                    // For hotel table: # | Country | Place | Check In | Check Out | Preferred Hotel | Traveller | Actions
                    if (
                      arrName === 'hotelInformation' &&
                      fieldName === 'country' &&
                      tdIndex === 1
                    ) {
                      target = selectTd;
                    }
                    // For flight table: # | From City | To Country | To City | Date | Pref. Time | Pref. Airline | Class | Traveller | Actions
                    else if (arrName === 'flightInformation') {
                      if (
                        (fieldName === 'fromCity' && tdIndex === 1) ||
                        (fieldName === 'toCountry' && tdIndex === 2) ||
                        (fieldName === 'toCity' && tdIndex === 3) ||
                        (fieldName === 'airline' && tdIndex === 6)
                      ) {
                        target = selectTd;
                      }
                    }
                  });
                }

                // Fallback to first td in the row
                if (!target) {
                  target = row.querySelector('td');
                }
                break;
              }
            }
          }
        }

        // a) try exact selectors first
        if (!target) {
          const exactSels = [
            `[name="${firstKey}"]`,
            `input[name="${firstKey}"]`,
            `select[name="${firstKey}"]`,
            `textarea[name="${firstKey}"]`,
            `#${firstKey}`,
          ];
          for (const sel of exactSels) {
            try {
              const foundElement = root.querySelector(sel);
              if (foundElement) {
                target = foundElement;
                break;
              }
            } catch {}
          }
        }

        // b) Enhanced array field targeting
        if (!target && isArray) {
          // Parse different array patterns
          const arrayPatterns = [
            // flightInformation[0].departure -> match flightInformation.0.departure
            /^(\w+)\[(\d+)\]\.(.+)$/,
            // flightInformation[0] -> match flightInformation.0
            /^(\w+)\[(\d+)\]$/,
            // nested arrays: items[0].subItems[1].field
            /^(\w+)\[(\d+)\]\.(\w+)\[(\d+)\]\.(.+)$/,
          ];

          for (const pattern of arrayPatterns) {
            const match = firstKey.match(pattern);
            if (match) {
              let selectors = [];

              if (pattern.source.includes('subItems')) {
                // Handle deeply nested arrays
                const [, arrName, idx1, subArr, idx2, fieldName] = match;
                selectors = [
                  // React Hook Form dot notation
                  `[name="${arrName}.${idx1}.${subArr}.${idx2}.${fieldName}"]`,
                  `input[name="${arrName}.${idx1}.${subArr}.${idx2}.${fieldName}"]`,
                  `select[name="${arrName}.${idx1}.${subArr}.${idx2}.${fieldName}"]`,
                  `textarea[name="${arrName}.${idx1}.${subArr}.${idx2}.${fieldName}"]`,
                  // Alternative bracket notations
                  `[name="${arrName}[${idx1}][${subArr}][${idx2}][${fieldName}]"]`,
                  `input[name="${arrName}[${idx1}][${subArr}][${idx2}][${fieldName}]"]`,
                  // Mixed notations
                  `[name="${arrName}[${idx1}].${subArr}[${idx2}].${fieldName}"]`,
                  `input[name="${arrName}[${idx1}].${subArr}[${idx2}].${fieldName}"]`,
                ];
              } else if (match.length === 4) {
                // Handle simple array fields: flightInformation[0].departure
                const [, arrName, idx, fieldName] = match;
                selectors = [
                  // React Hook Form dot notation (most common)
                  `[name="${arrName}.${idx}.${fieldName}"]`,
                  `input[name="${arrName}.${idx}.${fieldName}"]`,
                  `select[name="${arrName}.${idx}.${fieldName}"]`,
                  `textarea[name="${arrName}.${idx}.${fieldName}"]`,
                  // Alternative bracket notations
                  `[name="${arrName}[${idx}][${fieldName}]"]`,
                  `input[name="${arrName}[${idx}][${fieldName}]"]`,
                  `select[name="${arrName}[${idx}][${fieldName}]"]`,
                  `textarea[name="${arrName}[${idx}][${fieldName}]"]`,
                  // Mixed notations
                  `[name="${arrName}[${idx}].${fieldName}"]`,
                  `input[name="${arrName}[${idx}].${fieldName}"]`,
                  // ID selectors
                  `#${arrName}_${idx}_${fieldName}`,
                  `#${arrName}-${idx}-${fieldName}`,
                ];
              } else if (match.length === 3) {
                // Handle array without field: flightInformation[0]
                const [, arrName, idx] = match;
                selectors = [
                  `[name="${arrName}.${idx}"]`,
                  `[name="${arrName}[${idx}]"]`,
                  `#${arrName}_${idx}`,
                  `#${arrName}-${idx}`,
                ];
              }

              // Try all selectors for this pattern
              for (const sel of selectors) {
                try {
                  target = root.querySelector(sel);
                  if (target) break;
                } catch {}
              }
              if (target) break;
            }
          }

          // Additional fallback for array fields - partial matching
          if (!target) {
            const arrayMatch = firstKey.match(/^(\w+)\[(\d+)\](?:\.(.+))?$/);
            if (arrayMatch) {
              const [, arrName, idx, fieldName] = arrayMatch;

              // Try partial matches
              const partialSelectors = [
                // Find any field that starts with the array name and index
                `[name^="${arrName}.${idx}"]`,
                `[name^="${arrName}[${idx}]"]`,
                `input[name^="${arrName}.${idx}"]`,
                `input[name^="${arrName}[${idx}]"]`,
                // Find by data attributes if your form uses them
                `[data-field-path^="${arrName}.${idx}"]`,
                `[data-name^="${arrName}.${idx}"]`,
              ];

              if (fieldName) {
                // Also try to match the specific field name
                partialSelectors.push(
                  `input[name*=".${fieldName}"]`,
                  `select[name*=".${fieldName}"]`,
                  `textarea[name*=".${fieldName}"]`,
                  `[name$=".${fieldName}"]`,
                );
              }

              for (const sel of partialSelectors) {
                try {
                  target = root.querySelector(sel);
                  if (target) break;
                } catch {}
              }
            }
          }
        }

        // c) Generic fallback: match by simple field name
        if (!target) {
          const simple = firstKey
            .split(/[.\[\]]/)
            .filter(Boolean)
            .pop()
            ?.toLowerCase();
          if (simple) {
            for (const el of root.querySelectorAll('input, select, textarea')) {
              const hay = [
                el.name,
                el.id,
                el.placeholder,
                el.dataset?.fieldName,
              ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              if (hay.includes(simple)) {
                target = el;
                break;
              }
            }
          }
        }

        // d) Array container fallback - look for the array section
        if (!target && isArray) {
          const arrayMatch = firstKey.match(/^(\w+)\[(\d+)\]/);
          if (arrayMatch) {
            const [, arrName, idx] = arrayMatch;

            // Look for common array container patterns
            const containerSelectors = [
              `[data-array="${arrName}"][data-index="${idx}"]`,
              `[data-field-array="${arrName}"][data-index="${idx}"]`,
              `.${arrName}-item[data-index="${idx}"]`,
              `.${arrName}-${idx}`,
              `#${arrName}-${idx}`,
              `.array-item[data-array-name="${arrName}"][data-index="${idx}"]`,
              // Generic array item selectors
              `[data-testid="${arrName}-${idx}"]`,
              `[data-cy="${arrName}-${idx}"]`,
            ];

            for (const sel of containerSelectors) {
              try {
                target = root.querySelector(sel);
                if (target) {
                  // If we found a container, try to find the first input inside it
                  const firstInput = target.querySelector(
                    'input, select, textarea',
                  );
                  if (firstInput) {
                    target = firstInput;
                  }
                  break;
                }
              } catch {}
            }
          }
        }

        // e) final fallback: scroll to the error message itself or its container
        if (!target && message) {
          // find visible element with text exactly matching the error message
          const msgs = Array.from(
            root.querySelectorAll(
              '.invalid-feedback, .error-message, .field-error, .form-error, [role="alert"]',
            ),
          );
          const msgEl = msgs.find(m => m.textContent.trim() === message);
          if (msgEl) {
            // scroll to its nearest form-group or itself
            const container = msgEl.closest(
              '.form-group, .field-container, .input-group, .form-field, .mb-3, .col, .row, .array-item',
            );
            target = container || msgEl;
          }
        }

        // f) Handle hidden inputs - find the visible component or nearest table cell
        if (
          target &&
          (target.type === 'hidden' ||
            target.style.display === 'none' ||
            !target.offsetParent)
        ) {
          const hiddenTarget = target;
          target = null;

          // For table array fields, prioritize finding TD
          if (isTableArrayField) {
            const nearestTd = hiddenTarget.closest('td');
            if (nearestTd) {
              target = nearestTd;
            }
          }

          // If still no target, use original strategies
          if (!target) {
            const searchStrategies = [
              // 1. Look for parent container with the same name-based ID or class
              () => {
                const name = hiddenTarget.name;
                if (!name) return null;

                const selectors = [
                  // Common AsyncSelect container patterns
                  `#${name.replace(/\./g, '_')}-select`,
                  `#${name.replace(/\./g, '-')}-select`,
                  `.${name.replace(/\./g, '_')}-select`,
                  `.${name.replace(/\./g, '-')}-select`,
                  // React-Select specific
                  `[data-name="${name}"] .react-select__control`,
                  `[data-field="${name}"] .react-select__control`,
                  // Generic select alternatives
                  `select[data-name="${name}"]`,
                  `[role="combobox"][data-name="${name}"]`,
                ];

                for (const sel of selectors) {
                  try {
                    const el = root.querySelector(sel);
                    if (el && el.offsetParent) return el;
                  } catch {}
                }
                return null;
              },

              // 2. Look for siblings or nearby elements
              () => {
                const parent = hiddenTarget.parentElement;
                if (!parent) return null;

                // If parent is a td, return it
                if (parent.tagName.toLowerCase() === 'td') {
                  return parent;
                }

                // Look for visible form controls in the same container
                const visibleControls = parent.querySelectorAll(
                  'input:not([type="hidden"]), select, textarea, [role="combobox"], .react-select__control',
                );
                return (
                  Array.from(visibleControls).find(el => el.offsetParent) ||
                  null
                );
              },

              // 3. Look for AsyncSelect/React-Select components near the hidden input
              () => {
                const searchRadius = hiddenTarget.closest(
                  '.form-group, .field-container, .input-group, .form-field, .mb-3, .col, td',
                );
                if (!searchRadius) return null;

                // If we found a td as the container, return it
                if (searchRadius.tagName.toLowerCase() === 'td') {
                  return searchRadius;
                }

                const reactSelectControl = searchRadius.querySelector(
                  '.react-select__control, [class*="select__control"], [role="combobox"]',
                );
                return reactSelectControl && reactSelectControl.offsetParent
                  ? reactSelectControl
                  : null;
              },

              // 4. Look by proximity - find closest visible input to the hidden one
              () => {
                const allVisible = Array.from(
                  root.querySelectorAll(
                    'input:not([type="hidden"]), select, textarea, [role="combobox"]',
                  ),
                ).filter(el => el.offsetParent);

                if (allVisible.length === 0) return null;

                // Find the closest one by DOM position
                let closest = null;
                let minDistance = Infinity;

                for (const el of allVisible) {
                  try {
                    const distance =
                      Math.abs(el.tabIndex - hiddenTarget.tabIndex) ||
                      Array.from(root.querySelectorAll('*')).indexOf(el) -
                        Array.from(root.querySelectorAll('*')).indexOf(
                          hiddenTarget,
                        );
                    if (Math.abs(distance) < minDistance) {
                      minDistance = Math.abs(distance);
                      closest = el;
                    }
                  } catch {}
                }

                if (closest) {
                  // If the closest element is in a table cell, return the cell
                  const closestTd = closest.closest('td');
                  return closestTd || closest;
                }

                return null;
              },
            ];

            // Try each strategy until we find a visible target
            for (const strategy of searchStrategies) {
              try {
                const found = strategy();
                if (found) {
                  target = found;
                  break;
                }
              } catch (e) {}
            }

            // If still no visible target, try to scroll to the container
            if (!target) {
              const container = hiddenTarget.closest(
                'td, .form-group, .field-container, .input-group, .form-field, .mb-3, .col, .row',
              );
              if (container) {
                target = container;
              }
            }
          }
        }

        if (target) {
          scrollToElement(target);
        }

        return null;
      });
    });
  };

  // Simple scroll function with focus-style highlight for both TD and form fields
  function scrollToElement(element) {
    if (!element) return;

    // Add focus-style highlight for table cells AND form fields
    const isFormField = ['input', 'select', 'textarea'].includes(
      element.tagName.toLowerCase(),
    );
    const isTableCell = element.tagName.toLowerCase() === 'td';

    if (isTableCell || isFormField) {
      element.style.border = '1px solid #dc3545';
      element.style.boxShadow = '0 0 0 0.1rem rgba(220, 53, 69, 0.25)';

      setTimeout(() => {
        element.style.border = '';
        element.style.boxShadow = '';
      }, 2000);
    }

    // Scroll to element
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });

    // Try to focus if possible
    if (element.focus && typeof element.focus === 'function') {
      try {
        element.focus();
      } catch (e) {}
    }
  }

  const getBackNavigation = () => {
    if (isHr) {
      navigate('/hr/travel-enquiry');
    } else if (isFromEnquiry) {
      navigate('/enquiry/travel');
    } else {
      navigate('/travel-request');
    }
  };

  const getOptionNavigation = () => {
    if (isHr) {
      navigate(`/hr/travel-request/options/${requestNo}/${revisionNo}`);
    } else {
      navigate(`/travel-request/options/${requestNo}/${revisionNo}`);
    }
  };

  const renderViewButtons = () => (
    <>
      {renderButton({
        label: 'View Quote Option',
        onClick: getOptionNavigation,
        show: true, // always show in view mode
      })}
      {renderButton({
        label: 'View Approvers',
        onClick: toggleApproversModal,
        show: true,
      })}
      {renderButton({
        label: 'View Revisions',
        onClick: toggleRevisionsModal,
        className: 'custom-secondary-btn',
        show: true,
      })}
      {renderButton({
        label: 'View PO Details',
        onClick: togglePOModal,
        show: true,
      })}
    </>
  );

  // Render all buttons
  const renderAllButtons = () => {
    if (isView) return renderViewButtons();
    switch (true) {
      case isClone:
        return (
          <>
            {renderButton({
              type: 'submit',
              label: 'Save Details',
              disabled: isSubmitting || isSaveSubmitting,
              onClick: () => setSubmitOption('S'),
              show: showSave,
              loading:
                (isSubmitting || isSaveSubmitting) && submitOption === 'S',
              loadingText: 'Saving...',
              className: 'custom-secondary-btn',
            })}
            {renderButton({
              label: 'View Revisions',
              onClick: toggleRevisionsModal,
            })}
          </>
        );
      default:
        return (
          <>
            {showSave &&
              renderButton({
                type: 'submit',
                label: 'Save Details',
                disabled: isSubmitting || isSaveSubmitting,
                onClick: () => setSubmitOption('S'),
                show: showSave,
                loading:
                  (isSubmitting || isSaveSubmitting) && submitOption === 'S',
                loadingText: 'Saving...',
                className: 'custom-secondary-btn',
              })}

            {!isEdit &&
              renderButton({
                label: 'Clone Request',
                onClick: toggleCloneModal,
              })}

            {isEdit && (
              <>
                {renderButton({
                  type: 'submit',
                  label: 'Request for Quote',
                  disabled: isSubmitting || isSaveSubmitting,
                  onClick: () => setSubmitOption('Q'),
                  show: showRequestForQuote,
                  loading:
                    (isSubmitting || isSaveSubmitting) && submitOption === 'Q',
                })}

                {renderButton({
                  label: 'Cancel Quote Request',
                  onClick: toggleCancelQuoteModal,
                  show: showCancelQuoteRequest,
                })}

                {renderButton({
                  label: 'Cancel Travel Request',
                  className: 'custom-danger-btn',
                  onClick: toggleCancelTravelRequestModal,
                  show: showCancelTravelRequest,
                })}

                {renderButton({
                  label: 'Submit for Approval',
                  show: showSubmitForApproval,
                  onClick: handleSubmitApproval,
                  loading: selectTravelOptionPending,
                  loadingText: 'Submitting...',
                })}

                {renderButton({
                  label: 'View Quote Option',
                  onClick: getOptionNavigation,
                  show: showViewQuoteOption,
                })}

                {renderButton({
                  label: 'Modify Booking',
                  onClick: toggleModifyBookingModal,
                  show: showModifyBooking,
                })}

                {renderButton({
                  label: 'Cancel Booking',
                  className: 'custom-danger-btn',
                  onClick: toggleCancelBookingModal,
                  show: showCancelBooking,
                })}

                {renderButton({
                  label: 'View Approvers',
                  onClick: toggleApproversModal,
                  show: showViewApprovers,
                })}

                {renderButton({
                  label: 'View Revisions',
                  onClick: toggleRevisionsModal,
                  className: 'custom-secondary-btn',
                })}

                {renderButton({
                  label: 'View Risk Country Info',
                  onClick: toggleRiskCountryDetailsModal,
                  show: showViewRiskCountryInfo,
                })}

                {renderButton({
                  label: 'View PO Details',
                  onClick: togglePOModal,
                  show: showViewPODetails,
                })}
              </>
            )}
          </>
        );
    }
  };

  return (
    <div className="page-content">
      <div className="mb-3">
        <Form onSubmit={handleSubmit(onSubmit, onError)} innerRef={formRef}>
          {/* Header */}
          <div className="ntr-header d-flex justify-content-between align-items-center">
            <Col>
              {isEdit ? (
                <div>
                  <div className="d-flex flex-row align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-link p-0 back-btn fs-5"
                      onClick={() => getBackNavigation()}
                    >
                      <Icon path={mdiChevronLeft} size={1.1} />
                      <span className="d-flex flex-row gap-2 fs-5">Back</span>
                    </button>
                    <div className="trd-separator" />
                    <div className="d-flex flex-row gap-2 fs-5">
                      <span
                        className="fw-semibold fs-5"
                        style={{ color: '#065A86', fontSize: '14px' }}
                      >
                        Request ID
                      </span>
                      <span className="fw-semibold fs-5">
                        {requestNo}/{revisionNo}
                      </span>
                    </div>
                    <div className="trd-separator" />
                    <div className="d-flex flex-row align-items-center gap-2">
                      <span
                        className="fw-semibold fs-5"
                        style={{ color: '#065A86', fontSize: '14px' }}
                      >
                        Status
                      </span>
                    </div>
                    <Badge
                      config={
                        renderStatusColors?.[detailsData?.headerDetails?.Status]
                      }
                      dot
                      className="p-2"
                    >
                      {detailsData?.headerDetails?.Status}
                    </Badge>
                  </div>
                </div>
              ) : (
                <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                  <button
                    type="button"
                    className="btn btn-link p-0 back-btn "
                    onClick={() => navigate(-1)}
                  >
                    <Icon path={mdiChevronLeft} size={1.1} />
                    <span className="d-flex flex-row gap-2 fs-5">Back</span>
                  </button>
                  <div className="trd-separator" />
                  <div className="fw-semibold fs-5">New Travel Request</div>
                </div>
              )}
            </Col>
          </div>
          {/* Form */}
          <div className="p-0 d-flex flex-column gap-3 mb-3">
            {/* General Details */}
            <GeneralInformation
              detailsData={detailsData}
              isEdit={isEdit}
              isView={isView}
              control={control}
              errors={errors}
              setValue={setValue}
              watch={watch}
              travelType={travelType}
              toggleFamilyDetailsModal={toggleFamilyDetailsModal}
              isCommonOptionsLoading={isCommonOptionsLoading}
              commonOptions={commonOptions}
            />

            {/* Requirements */}
            <Requirements
              isEdit={isEdit}
              isView={isView}
              detailsData={detailsData}
              control={control}
              watch={watch}
              setValue={setValue}
            />

            {/* Flight Information */}
            {requirements.flight && (
              <FlightInformation
                isEdit={isEdit}
                isView={isView}
                control={control}
                errors={errors}
                setValue={setValue}
                detailsData={detailsData}
                getValues={getValues}
              />
            )}

            {/* Hotel Information */}
            {requirements.hotel && (
              <HotelInformation
                isEdit={isEdit}
                isView={isView}
                control={control}
                errors={errors}
                setValue={setValue}
                detailsData={detailsData}
              />
            )}

            {travellerType && ['I102', 'I103'].includes(travellerType) && (
              <AdditionalInformation isView={isView} control={control} />
            )}

            {/* Client Information */}
            {travellerType &&
              ['B102', 'B103', 'T102', 'T103', 'I102', 'I103'].includes(
                travellerType,
              ) && (
                <ClientInformation
                  isEdit={isEdit}
                  isView={isView}
                  control={control}
                  errors={errors}
                  detailsData={detailsData}
                />
              )}

            {/* Emergency Contact */}
            <EmergencyContact
              isView={isView}
              control={control}
              errors={errors}
            />

            {showCost && (
              <ShowCost
                control={control}
                headerDetails={detailsData?.headerDetails}
                isClone={isClone}
              />
            )}

            {/* Upload Attachments */}
            {isEdit && (
              <UploadAttachments
                isView={isView}
                isCommonOptionsLoading={isCommonOptionsLoading}
                commonOptions={commonOptions}
                detailsData={detailsData}
                detailsLoading={detailsLoading}
                requestNo={requestNo}
                refetchTravelRequestDetails={refetchTravelRequestDetails}
                isClone={isClone}
                id={requestNo}
              />
            )}
            <NewFamilyDetailsModal
              isEdit={isEdit}
              isOpen={familyDetailsModal}
              toggle={toggleFamilyDetailsModal}
              setValue={setValue}
              detailsData={detailsData}
            />
            <ViewApproversModal
              isOpen={viewApproversModal}
              toggle={toggleApproversModal}
              requestNo={requestNo}
              revisionNo={revisionNo}
            />
            <ViewRevisionsModal
              isOpen={viewRevisionsModal}
              toggle={toggleRevisionsModal}
              requestNo={requestNo}
              revisionNo={revisionNo}
            />
            <ViewPOModal
              isOpen={viewPOModal}
              toggle={togglePOModal}
              requestNo={requestNo}
              revisionNo={revisionNo}
            />
            <CancelQuoteRequest
              isOpen={cancelQuoteModal}
              toggle={toggleCancelQuoteModal}
              reqNo={requestNo}
              revisionNo={revisionNo}
            />
            <CancelTravelRequest
              isOpen={cancelTravelRequestModal}
              toggle={toggleCancelTravelRequestModal}
              reqNo={requestNo}
              revisionNo={revisionNo}
            />
            <ModifyBooking
              isOpen={modifyBookingModal}
              toggle={toggleModifyBookingModal}
              reqNo={requestNo}
              revisionNo={revisionNo}
            />
            <CloneRequestModal isOpen={cloneModal} toggle={toggleCloneModal} />
          </div>
          <QuotePendingModal
            isOpen={quotePendingModal}
            toggle={toggleQuotePendingModal}
            revisionId={revisionNo}
            requestId={requestNo}
          />
          <RiskCountryModal
            isOpen={riskCountryModal}
            toggle={toggleRiskCountryModal}
            country={riskCountryName}
            onConfirm={() => {
              setRiskCountryModal(false);
              if (pendingPayload) {
                submitRequest(pendingPayload);
                setPendingPayload(null);
              }
            }}
            onCancel={() => {
              setRiskCountryModal(false);
              setPendingPayload(null);
            }}
          />
          <RiskCountryDetails
            isOpen={riskCountryDetailsModal}
            reqNo={requestNo}
            revisionNo={revisionNo}
            toggle={toggleRiskCountryDetailsModal}
            onConfirm={() => {
              setRiskCountryDetailsModal(false);
              if (pendingPayload) {
                submitRequest(pendingPayload);
                setPendingPayload(null);
              }
            }}
            onCancel={() => {
              setRiskCountryDetailsModal(false);
              setPendingPayload(null);
            }}
            riskData={detailsData?.riskDetails || []}
          />
          <CancelBooking
            isOpen={cancelBookingModal}
            toggle={toggleCancelBookingModal}
            reqNo={requestNo}
            revisionNo={revisionNo}
          />
          <Col>
            <div className="d-flex flex-row align-items-center justify-content-center gap-2">
              {renderAllButtons()}
            </div>
          </Col>
        </Form>
      </div>
    </div>
  );
}

export default NewTravelRequest;
